import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import moment from 'moment-timezone';
import { AlertService } from '../../_alert';
import { DatastoreService } from '../../services/datastore.service';
import { ApiService } from '../../services/jrni/api.service';
import { ServicesService } from '../../services/jrni/services.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { DepartmentService } from '../../services/jrni/department.service';
import { QuestionsService } from '../../services/questions.service';
import { CivicaService } from '../../services/civica.service';
import { AppConfig } from '../../app.config';

/**
 * Civica payment return leg.
 *
 * Primary flow — Civica eStore PRE-BASKET (book-after-payment):
 *   The gateway returns to `payment-verify/:company/:basket/:serviceItems`.
 *   No booking exists yet — bl-civica-api stored the intent and, once Civica
 *   confirms payment, the shared bl-payment-logs worker checks out the JRNI
 *   basket and links the extra items. This component polls
 *   /payment/civica/pre-basket/find until the row shows the JRNI purchase
 *   (data.purchase_id / status PAID), then hydrates and navigates to
 *   confirmation. A FAILED_ or CANCELLED status routes to estore-fail.
 *
 * Legacy fallback — Civica eStore POST-BASKET (SRPO-169):
 *   If the bare `payment-verify` route is hit with querystring params
 *   (ref / ResponseCode / BasketRef) the older return handling runs, marking
 *   the already-created purchase paid. Retained for backwards compatibility.
 */
@Component({
  selector: 'app-verify-payment',
  templateUrl: './verify-payment.component.html',
  styleUrls: ['./verify-payment.component.scss'],
})
export class VerifyPaymentComponent implements OnInit, OnDestroy {
  pageTitle = 'Verifying payment';
  verifyPageText = 'Please wait while we confirm your payment...';
  paymentFailed = false;
  maxRetriesReached = false;

  companyId: any = null;
  basket: string = null;
  serviceItem: string = null;

  private paymentId: any = null;
  private bookingId: any = null;
  private amount: any = null;
  private selectedLocation: any;

  private pollSub: Subscription;
  private maxRetries = 300;
  private initialDataExtracted = false;
  private findErrorCount = 0;
  private maxFindErrors = 10;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private alertService: AlertService,
    private translateService: TranslateService,
    private datastoreService: DatastoreService,
    private apiService: ApiService,
    private servicesService: ServicesService,
    private availabilityService: AvailabilityService,
    private departmentService: DepartmentService,
    private questionsService: QuestionsService,
    private civicaService: CivicaService
  ) {}

  async ngOnInit() {
    this.spinner.show();

    // Prefer the pre-basket route (company/basket/serviceItems path segments).
    this.route.params.subscribe(async (params) => {
      if (params.company) {
        this.companyId = params.company;
      }
      if (params.basket) {
        this.basket = params.basket;
      }
      if (params.serviceItems) {
        const items = params.serviceItems.split(',').filter((s) => s);
        if (items.length > 0) {
          this.serviceItem = items[0];
        }
      }

      if (this.basket && this.serviceItem) {
        await this.initPreBasket();
      } else {
        // No path params — fall back to the legacy eStore querystring flow.
        this.route.queryParams.subscribe((qp) => this.initLegacyEstore(qp));
      }
    });
  }

  ngOnDestroy() {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
  }

  // ---- Civica pre-basket (book-after-payment) ----

  private async initPreBasket() {
    console.log(
      'Verify payment (pre-basket) — company:',
      this.companyId,
      'basket:',
      this.basket,
      'serviceItem:',
      this.serviceItem
    );

    try {
      this.datastoreService.config = await this.apiService.getConfig(
        AppConfig.settings.core.company_id
      );
    } catch (err) {
      console.error('Error loading config:', err);
    }

    // Resolve the location so the confirmation page can find it; prefer the
    // child company the booking sits under, fall back to the parent.
    try {
      const parent = await this.departmentService.getParentDepartment();
      await this.departmentService.setLocation(parent);
      this.selectedLocation = parent;
      try {
        const children = await this.departmentService.getChildDepartments(
          parent
        );
        for (const location of children) {
          if (location.id == this.companyId) {
            await this.departmentService.setLocation(location);
            this.selectedLocation = location;
            break;
          }
        }
      } catch (err) {
        console.error('Error resolving child location:', err);
      }
    } catch (err) {
      console.error('Error resolving location:', err);
    }

    this.startFindPolling();
  }

  private startFindPolling() {
    // Poll immediately, then every 2 seconds until the backend worker has
    // created the JRNI booking (or reports a failure).
    const source = timer(0, 2000);
    this.pollSub = source.subscribe(async (attempt) => {
      if (attempt >= this.maxRetries) {
        this.pollSub.unsubscribe();
        this.spinner.hide();
        this.maxRetriesReached = true;
        this.pageTitle = 'Payment verification timed out';
        this.verifyPageText =
          'We were unable to confirm your payment in time. Please contact the register office for assistance.';
        return;
      }

      try {
        const found: any = await this.civicaService.preBasketFind(
          this.companyId,
          this.basket,
          this.serviceItem
        );
        console.log(`preBasketFind (attempt ${attempt + 1}):`, found);

        if (found && found.data) {
          if (!this.initialDataExtracted) {
            await this.extractInitialData(found);
            this.initialDataExtracted = true;
          }

          const status = `${found.status || ''}`;

          // Checkout / processing failures.
          if (status.indexOf('FAILED') !== -1 || status === 'CANCELLED') {
            this.pollSub.unsubscribe();
            this.handlePreBasketFailure(found);
            return;
          }

          // purchase_id is written once the worker has checked out the basket
          // and created the JRNI booking (BOOKING_MADE / PAID).
          if (found.data.purchase_id || status === 'PAID') {
            this.pollSub.unsubscribe();
            await this.hydrateAndNavigate(found);
            return;
          }

          // Payment confirmed by Civica but booking not yet created.
          if (found.data.payment_notification) {
            this.verifyPageText =
              'Payment received. Finalising your booking, please wait...';
          }
        }
      } catch (err) {
        console.error('Error in preBasketFind polling:', err);
        this.findErrorCount++;
        if (this.findErrorCount >= this.maxFindErrors) {
          this.pollSub.unsubscribe();
          this.spinner.hide();
          this.pageTitle = 'Payment verification error';
          this.verifyPageText = this.translateService.instant('PAYMENT.ERROR');
          this.paymentFailed = true;
        }
      }
    });
  }

  private async extractInitialData(found: any) {
    const data = found.data;
    if (!data) return;

    if (data.extra_items?.lookupResponses) {
      this.datastoreService.lookupResponses = data.extra_items.lookupResponses;
    }
    if (data.extra_items?.selectedAddress) {
      this.datastoreService.selectedAddress = data.extra_items.selectedAddress;
    }

    if (data.auth_token) {
      localStorage.setItem('auth_token', data.auth_token);
    }

    if (data.journey_route) {
      this.datastoreService.journeyRoute = data.journey_route;
      const content =
        this.datastoreService.config?.journey_config?.appointments?.page_content?.[
          data.journey_route
        ];
      this.datastoreService.pageContent = content;
    }

    if (data.detail_group) {
      this.getCompanyQuestions(data.detail_group);
    }

    if (data.slot) {
      this.datastoreService.selectedSlot = data.slot;
      await this.availabilityService.setSelectedSlot(data.slot);
    }
    this.datastoreService.extraDuration = data.extra_duration || 0;

    if (data.extra_items?.fees) {
      this.datastoreService.serviceFees = data.extra_items.fees;
    }
  }

  private async hydrateAndNavigate(found: any) {
    try {
      const data = found.data;
      const location =
        this.selectedLocation || (await this.departmentService.getLocation());

      const purchase: any = await this.apiService.findPurchase(
        location,
        data.purchase_id
      );
      const booking = purchase._embedded.bookings[0];
      this.datastoreService.booking = booking;
      this.datastoreService.purchase = purchase;

      const service: any = await this.apiService.getService(
        location,
        booking.service_id
      );
      this.datastoreService.selectedService = service;
      await this.servicesService.set(service);

      localStorage.setItem('booking', JSON.stringify(booking));
      localStorage.setItem(
        'date',
        JSON.stringify(this.datastoreService.selectedSlot)
      );
      localStorage.setItem('location', JSON.stringify(location));
      localStorage.setItem('service', JSON.stringify(service));

      this.spinner.hide();
      this.router.navigate(['confirmation']);
    } catch (err) {
      console.error('Error hydrating confirmation data:', err);
      this.spinner.hide();
      this.pageTitle = 'Payment verification error';
      this.verifyPageText = this.translateService.instant('PAYMENT.ERROR');
      this.paymentFailed = true;
    }
  }

  private handlePreBasketFailure(found: any) {
    try {
      this.apiService.logPaymentExtra(
        { id: this.companyId },
        { id: found?.data?.service_id },
        found?.data?.purchase_id,
        {
          basket: this.basket,
          service_item: this.serviceItem,
          pre_basket_status: found?.status,
          payment_notification: found?.data?.payment_notification,
        }
      );
    } catch (err) {
      console.error(err);
    }
    localStorage.setItem(
      'estoreError',
      JSON.stringify('Your payment has not been successful')
    );
    this.spinner.hide();
    this.router.navigate(['estore-fail']);
  }

  private getCompanyQuestions(detailGroup) {
    this.questionsService
      .getCompanyQuestions(detailGroup)
      .subscribe((res: any) => {
        this.datastoreService.companyQuestions = res.questions;
      });
  }

  // ---- Legacy Civica eStore post-basket return leg (SRPO-169) ----

  private async initLegacyEstore(params) {
    if (!params['ref'] && !params['ResponseCode']) {
      console.error('Missing eStore return params');
      this.spinner.hide();
      this.pageTitle = 'Payment verification error';
      this.verifyPageText = 'Missing payment details. Please try again.';
      this.paymentFailed = true;
      return;
    }

    try {
      if (params['ref']) {
        const dataObj = JSON.parse(atob(params['ref']));
        this.bookingId = dataObj['bookingIds'];
        this.paymentId = dataObj['purchase_id'];
        this.companyId = dataObj['companyId'];
        this.amount = dataObj['amount'];
      }
    } catch (err) {
      console.error('Could not decode eStore ref param', err);
    }

    try {
      this.datastoreService.config = await this.apiService.getConfig(
        AppConfig.settings.core.company_id
      );
    } catch (err) {
      console.error('Error loading config:', err);
    }

    try {
      const parent = await this.departmentService.getParentDepartment();
      await this.departmentService.setLocation(parent);
      this.selectedLocation = parent;
      try {
        const children = await this.departmentService.getChildDepartments(
          parent
        );
        for (const location of children) {
          if (location.id == this.companyId) {
            await this.departmentService.setLocation(location);
            this.selectedLocation = location;
            break;
          }
        }
      } catch (err) {
        console.error('Error resolving child location:', err);
      }
    } catch (err) {
      console.error('Error resolving location:', err);
    }

    await this.verifyLegacyPayment(params);
  }

  private async verifyLegacyPayment(params) {
    try {
      const booking = JSON.parse(localStorage.getItem('booking'));
      this.datastoreService.booking = booking;

      const service: any = await this.apiService.getService(
        { id: booking.company_id },
        booking.service_id
      );
      this.datastoreService.selectedService = service;
      await this.servicesService.set(service);

      const slot = { start: booking.datetime };
      this.datastoreService.selectedSlot = slot;
      await this.availabilityService.setSelectedSlot(slot);

      const purchaseId = this.paymentId || booking.purchase_id;
      const purchase: any = await this.apiService.findPurchase(
        { id: booking.company_id },
        purchaseId
      );
      const member = purchase._embedded.client;
      this.datastoreService.purchase = purchase;

      if (
        params['ResponseCode'] == '00001' ||
        params['ResponseCode'] == '99999'
      ) {
        this.logVerifyIssue(booking, params);
        localStorage.setItem(
          'estoreError',
          JSON.stringify(
            params['ResponseDescription'] ||
              'Your payment has not been successful'
          )
        );
        this.router.navigate(['estore-fail']);
        return;
      }

      if (params['ResponseCode'] == '00002') {
        await this.rollbackBooking(booking);
        return;
      }

      try {
        await this.apiService.payPurchase(
          booking.company_id,
          booking.service_id,
          booking.purchase_id,
          {
            amount: booking.price,
            transaction_id: `${params['BasketRef'] || ''}`,
            notify: false,
            notify_admin: false,
            payment_status: 'paid',
            notes: 'Appointment fee',
          },
          member.id
        );
      } catch (err) {
        console.error(err);
      }

      try {
        let note = `Payment type: Appointment fee`;
        note += ` | Payment date: ${moment()
          .tz('Europe/London')
          .format('dddd, MMMM Do YYYY')}`;
        note += ` | Payment amount: £${(
          parseInt(booking.price, 10) / 100
        ).toFixed(2)}`;
        if (params['BasketRef']) {
          note += ` | Receipt number: ${params['BasketRef']}`;
        }
        await this.apiService.addNoteToBooking(
          { id: booking.company_id },
          booking.id,
          note
        );
      } catch (err) {
        console.error(err);
      }

      try {
        const config: any = this.datastoreService.config;
        if (config?.journey_config?.crm_push === 'osc') {
          // Appointments MUST send form_schema + answers, or bl-jrni-api builds the
          // CEREMONIES incident instead — that reads booking.questions[61] and throws,
          // so the contact is created and the Service Request never is. This leg is a
          // fresh page load after the eStore redirect, so prefer the datastore but fall
          // back to what summary stashed in localStorage beside the booking.
          let formSchema: string = this.datastoreService.selectedSchema;
          let answers: any[] = this.datastoreService.bookingAnswers;
          if (!formSchema) {
            formSchema = localStorage.getItem('bookingSchema') || '';
          }
          if (!Array.isArray(answers) || !answers.length) {
            try {
              answers = JSON.parse(localStorage.getItem('bookingAnswers') || '[]');
            } catch (err) {
              answers = [];
            }
          }
          await this.apiService.submitCaseToOracle(
            { id: booking.company_id },
            booking,
            formSchema,
            answers
          );
        }
      } catch (err) {
        console.error(err);
      }

      this.spinner.hide();
      this.router.navigate(['confirmation']);
    } catch (err) {
      console.error(err);
      this.spinner.hide();
      this.pageTitle = 'Payment verification error';
      this.verifyPageText = this.translateService.instant('PAYMENT.ERROR');
      this.paymentFailed = true;
    }
  }

  private logVerifyIssue(booking, params) {
    try {
      this.apiService.logPaymentExtra(
        { id: booking.company_id },
        { id: booking.service_id },
        booking.purchase_id,
        {
          purchase: booking.purchase_id,
          payment_verify_error: {
            ResponseCode: params['ResponseCode'],
            ResponseDescription: params['ResponseDescription'],
            BasketRef: params['BasketRef'],
          },
        }
      );
    } catch (err) {
      console.error(err);
    }
  }

  private async rollbackBooking(booking) {
    try {
      await this.apiService.cancelMultiBookings(booking.company_id, booking.id);
    } catch (err) {
      console.error(err);
    }
    localStorage.removeItem('booking');
    localStorage.removeItem('estoreUrl');
    this.spinner.hide();
    this.restartJourney();
  }

  restartJourney() {
    if (sessionStorage.getItem('journey')) {
      this.router.navigate(['start/' + sessionStorage.getItem('journey')]);
    } else {
      this.router.navigate(['/start']);
    }
  }
}
